import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'sugargoo-coupon-guide')!;

export default function CouponGuide() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: Saving Money on Sugargoo Orders
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Smart shoppers know that finding great products is only half the battle — the other half is minimizing costs. This comprehensive <strong>Sugargoo coupon and discount code guide</strong> reveals every legitimate way to save money on your orders, from official platform promotions to strategic shopping techniques that add up to real savings.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          We have compiled the most current discount opportunities, explained how promotional systems work, and shared insider tips from experienced Sugargoo users who have mastered the art of cost-efficient international shopping.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Whether you are a first-time buyer or a seasoned haul shopper, the strategies in this guide will help you stretch your budget further without compromising on product quality or shipping reliability.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Types of Sugargoo Discounts Available
        </h2>
        <div className="space-y-4">
          {[
            {
              title: 'Shipping Fee Coupons',
              desc: 'The most common type of Sugargoo promotion. These coupons directly reduce your international shipping costs by a percentage (typically 5-15%) or a fixed amount. They are usually applied at checkout when you submit your package for shipment.',
            },
            {
              title: 'Service Fee Waivers',
              desc: 'Occasionally, Sugargoo runs promotions that waive or reduce the per-order service fee. These are particularly valuable for small orders where the service fee represents a larger percentage of the total cost.',
            },
            {
              title: 'New User Bonuses',
              desc: 'First-time users sometimes receive welcome credits or discounts after completing account verification. These promotions are designed to encourage trial of the platform and typically apply to your first few orders.',
            },
            {
              title: 'Holiday & Seasonal Sales',
              desc: 'Major shopping events like Singles Day (11.11), Black Friday, and Chinese New Year often trigger platform-wide promotions. Planning your purchases around these events can lead to substantial cumulative savings.',
            },
            {
              title: 'Referral Programs',
              desc: 'Some shopping agent platforms offer referral bonuses where both the referrer and new user receive credits. While Sugargoo referral program details change over time, keeping an eye on their announcements can reveal these opportunities.',
            },
            {
              title: 'Loyalty & Volume Rewards',
              desc: 'High-volume shoppers may qualify for tiered benefits including reduced service fees, priority warehouse processing, or exclusive shipping discounts. These are typically unadvertised and revealed through customer service.',
            },
          ].map((type) => (
            <div key={type.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-coupon-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{type.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{type.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Where to Find Active Sugargoo Promo Codes
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Finding valid, current Sugargoo coupons requires knowing where to look. Here are the most reliable sources:
        </p>
        <div className="space-y-3 mb-4">
          {[
            {
              source: 'Sugargoo Official Platform',
              detail: 'Check the announcements section, banner notifications, and your account dashboard. Official promotions are always posted here first.',
            },
            {
              source: 'Social Media Channels',
              detail: 'Follow Sugargoo official accounts on platforms where they are active. Flash sales and limited-time codes are often announced through these channels.',
            },
            {
              source: 'Community Forums & Groups',
              detail: 'Experienced users frequently share working codes and promotion alerts in community spaces. These are crowdsourced and often reveal codes before official announcements.',
            },
            {
              source: 'Partner & Affiliate Sites',
              detail: 'Some content creators and partner sites have exclusive codes negotiated with Sugargoo. These can sometimes offer better discounts than publicly available codes.',
            },
            {
              source: 'Email Notifications',
              detail: 'Enable email notifications in your Sugargoo account settings. Promotional emails often contain subscriber-exclusive codes.',
            },
          ].map((item) => (
            <div key={item.source} className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                <i className="ri-search-line w-3 h-3 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800">{item.source}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          <strong>Pro tip:</strong> Always verify the expiration date of any coupon code before relying on it for your order planning. Codes from community sources may be expired or have usage limits.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          How to Apply Sugargoo Coupon Codes
        </h2>
        <div className="space-y-4">
          {[
            {
              step: 'Copy the Code',
              desc: 'When you find a valid Sugargoo coupon code, copy it exactly — including any capitalization or special characters. Some codes are case-sensitive.',
            },
            {
              step: 'Navigate to Checkout',
              desc: 'After your items arrive at the warehouse and you are ready to ship, proceed to the shipping submission page. This is where coupon fields typically appear.',
            },
            {
              step: 'Enter the Code',
              desc: 'Look for the coupon or promo code input field in the shipping checkout interface. Paste your code and click apply or confirm.',
            },
            {
              step: 'Verify the Discount',
              desc: 'Before finalizing payment, double-check that the discount has been properly applied to your shipping total. If the code is invalid or expired, you will usually see an error message.',
            },
            {
              step: 'Complete Payment',
              desc: 'With the discount confirmed, proceed to pay the remaining balance. The savings will be reflected in your final charge.',
            },
          ].map((item, i) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{item.step}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Advanced Money-Saving Strategies Beyond Coupons
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          While coupons provide direct discounts, the biggest savings often come from smart shopping strategies:
        </p>
        <div className="space-y-4">
          {[
            {
              title: 'Consolidate Shipments Strategically',
              desc: 'Shipping 5 items together costs significantly less per item than shipping 5 separate packages. Plan your purchases so items arrive at the warehouse around the same time.',
            },
            {
              title: 'Remove Packaging at Warehouse',
              desc: 'Request that Sugargoo remove shoe boxes, tags, and excess packaging before shipping. This reduces volumetric weight and can cut shipping costs by 20-40%.',
            },
            {
              title: 'Choose Economy Shipping for Non-Urgent Items',
              desc: 'SAL and EUB can cost 50-70% less than express options. If you are not in a rush, the savings are substantial — often more than any coupon discount.',
            },
            {
              title: 'Buy During Marketplace Sales',
              desc: 'Chinese marketplaces run major sales events throughout the year. Combining marketplace discounts with Sugargoo coupons creates double savings on the same order.',
            },
            {
              title: 'Use the Right Payment Method',
              desc: 'Some payment methods have lower fees than others. Compare the total cost including payment processor fees when topping up your Sugargoo balance.',
            },
            {
              title: 'Plan Around Free Storage Periods',
              desc: 'Sugargoo typically offers 90-180 days of free warehouse storage. Use this window to collect multiple orders before shipping, but avoid letting items sit so long that you incur storage fees.',
            },
          ].map((strategy) => (
            <div key={strategy.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                <i className="ri-lightbulb-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{strategy.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{strategy.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Common Coupon Mistakes to Avoid
        </h2>
        <div className="space-y-3">
          {[
            'Using expired codes — always check the validity period before attempting to apply',
            'Trying to stack multiple coupons when only one is allowed per order',
            'Forgetting to apply the code before completing payment — most systems do not retroactively apply discounts',
            'Relying on unofficial code sites that list fake or expired codes to generate traffic',
            'Ignoring minimum order requirements — many coupons only work above a certain shipping total',
            'Not reading terms and conditions — some codes exclude specific shipping methods or destinations',
          ].map((mistake, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 shrink-0 mt-0.5">
                <i className="ri-error-warning-line w-3 h-3 flex items-center justify-center"></i>
              </div>
              <span className="text-sm text-foreground-700">{mistake}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Conclusion: Maximize Every Order
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          <strong>Sugargoo coupons and discount codes</strong> are valuable tools for reducing your international shopping costs, but they are just one piece of the savings puzzle. The most cost-efficient shoppers combine coupon discounts with strategic shipping consolidation, packaging removal, economy shipping selection, and marketplace sale timing.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          By following the strategies in this guide, you can realistically reduce your total order costs by 25-40% compared to unplanned, individual-item shipping with premium methods. The key is planning ahead: browse a <strong>Sugargoo Spreadsheet</strong> to find products, watch for promotional periods, consolidate intelligently, and always verify your coupon before checkout.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Happy shopping — and happy saving!
        </p>
      </section>
    </ArticleLayout>
  );
}